import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", email: "", service: "", address: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in your name, phone, email and service.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success(`Request received! Ref #${data.reference}. We'll be in touch shortly.`);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-black/15 py-3 text-base md:text-lg placeholder:text-neutral-400 focus:border-[#007AFF] focus:outline-none transition-colors";

  return (
    <section id="quote" data-testid="quote" className="py-20 md:py-32 bg-white border-t border-black/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">Get started</p>
          <h2 className="font-display font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
            Request your<br /><span className="brand-gradient-text">free quote.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-neutral-600 max-w-md">
            Tell us what's going on and we'll get right back to you. For emergencies, call us any time.
          </p>
          <div className="mt-8 space-y-2 text-neutral-700">
            <a href={COMPANY.phoneHref} className="block text-2xl font-display font-bold hover:text-[#007AFF] transition-colors">{COMPANY.phone}</a>
            <a href={COMPANY.emailHref} className="block text-neutral-600 hover:text-[#FF3B30] transition-colors break-all">{COMPANY.email}</a>
            <p className="text-neutral-500 pt-2">{COMPANY.addressLine1}<br />{COMPANY.addressLine2}, {COMPANY.country}</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="quote-success"
              className="rounded-3xl border border-black/10 bg-[#FAFAFA] p-10 md:p-14 text-center"
            >
              <CheckCircle2 className="h-14 w-14 mx-auto text-[#007AFF]" />
              <h3 className="mt-6 text-2xl md:text-3xl font-display font-bold">Request sent!</h3>
              <p className="mt-3 text-neutral-600 max-w-md mx-auto">Thanks for reaching out. We've received your details and will get back to you as soon as possible.</p>
              <button
                data-testid="quote-reset"
                onClick={() => setDone(false)}
                className="mt-7 inline-flex rounded-full border border-black/15 px-6 py-3 font-semibold hover:border-[#007AFF] hover:text-[#007AFF] transition-colors"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Name *</label>
                <input data-testid="quote-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" className={field} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Phone *</label>
                <input data-testid="quote-phone" value={form.phone} onChange={update("phone")} placeholder="(647) 000-0000" className={field} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Email *</label>
                <input data-testid="quote-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={field} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Service *</label>
                <select data-testid="quote-service" value={form.service} onChange={update("service")} className={`${field} appearance-none`}>
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                  <option value="Emergency / Other">Emergency / Other</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Address</label>
                <input data-testid="quote-address" value={form.address} onChange={update("address")} placeholder="Street, City" className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">How can we help?</label>
                <textarea data-testid="quote-message" value={form.message} onChange={update("message")} rows={3} placeholder="Describe the issue or project…" className={`${field} resize-none`} />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="quote-submit"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-2 rounded-full bg-black text-white font-semibold px-8 py-4 hover:bg-[#007AFF] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send Request <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
